import { useEffect, useMemo, useState } from 'react';
import turfBbox from '@turf/bbox';
import { atom, useAtomValue } from 'jotai';
import type { MapRef } from 'react-map-gl';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import Map from 'components/map/map';
import { PolygonArea } from 'components/map';
import { Line } from 'components/map/well';
import { buildPolygonStyle } from 'lib/map';
import type { ScenarioMapProps, ScenarioMapFeature } from './types';
import styles from './scenario-map.module.scss';

const DEFAULT_CENTER = { longitude: -99.25, latitude: 31.25 };
const DEFAULT_ZOOM = 5;

const scenarioMapRefAtom = atom<MapRef | null>(null);

const BASE_STYLE = buildPolygonStyle({
  color: '#a8a8a8',
  fillOpacity: 0.15,
  lineOpacity: 0.6,
  lineWidth: 1,
});

const FOCUSED_STYLE = buildPolygonStyle({
  color: '#0f62fe',
  fillOpacity: 0.3,
  lineOpacity: 0.8,
  lineWidth: 2,
});

const WELL_STICK_LINE_STYLE = {
  'line-color': '#da1e28',
  'line-width': 2.5,
};

const WELL_STICK_LINE_BASE_STYLE = {
  'line-color': '#a8a8a8',
  'line-width': 1.5,
};

/**
 * Returns whether a feature is currently focused.
 * @param {ScenarioMapFeature} feature - The feature to check
 * @param {number | string | null | undefined} focusedId - The ID of the focused feature
 * @return {boolean} True if the feature is focused
 */
function isFocusedFeature(
  feature: ScenarioMapFeature,
  focusedId: number | string | null | undefined
): boolean {
  return (
    focusedId === null || focusedId === undefined || feature.id === focusedId
  );
}

/**
 * Shared Mapbox GL map for scenario forms.
 * Renders polygon features (Area/Unit) and well-stick features (Slot)
 * with focus/dim behavior based on sidebar selection.
 * @param {ScenarioMapProps} props - The component props
 * @return {JSX.Element} The map accordion
 */
export function ScenarioMap({
  features,
  focusedId,
  mapConfigAtom,
}: ScenarioMapProps): JSX.Element {
  const [expanded, setExpanded] = useState(true);
  const mapRef = useAtomValue(scenarioMapRefAtom);

  const validFeatures = useMemo(
    () =>
      features.filter(
        (f): f is ScenarioMapFeature & { geometry: GeoJSON.Geometry } =>
          f.geometry !== null
      ),
    [features]
  );

  const bounds = useMemo(() => {
    if (validFeatures.length === 0) return null;
    const collection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: validFeatures.map((f) => ({
        type: 'Feature' as const,
        properties: {},
        geometry: f.geometry,
      })),
    };
    const [minLng, minLat, maxLng, maxLat] = turfBbox(
      collection as Parameters<typeof turfBbox>[0]
    );
    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ] as [[number, number], [number, number]];
  }, [validFeatures]);

  useEffect(() => {
    if (!mapRef || !bounds) return;
    mapRef.getMap().fitBounds(bounds, { padding: 40, maxZoom: 15 });
  }, [mapRef, bounds]);

  return (
    <Accordion
      expanded={expanded}
      onClick={() => setExpanded(!expanded)}
      className={styles.accordion}
    >
      <AccordionSummary className={styles.mapSummary}>
        <h4 className={styles.mapTitle}>Map</h4>
      </AccordionSummary>
      <AccordionDetails>
        <div className={styles.mapContainer}>
          <Map
            className={styles.mapFill}
            longitude={DEFAULT_CENTER.longitude}
            latitude={DEFAULT_CENTER.latitude}
            zoom={DEFAULT_ZOOM}
            mapConfigAtom={mapConfigAtom}
            refAtom={scenarioMapRefAtom}
            showControls
            showMapFilters
          >
            {validFeatures.map((feature) => {
              const focused = isFocusedFeature(feature, focusedId);

              if (feature.type === 'well-stick') {
                return (
                  <Line
                    key={feature.id}
                    id={`scenario-stick-${feature.id}`}
                    geojson={{
                      type: 'Feature',
                      properties: {},
                      geometry: feature.geometry,
                    }}
                    style={
                      focused
                        ? WELL_STICK_LINE_STYLE
                        : WELL_STICK_LINE_BASE_STYLE
                    }
                  />
                );
              }

              return (
                <PolygonArea
                  key={feature.id}
                  id={`scenario-feature-${feature.id}`}
                  geometry={feature.geometry}
                  style={focused ? FOCUSED_STYLE : BASE_STYLE}
                />
              );
            })}
          </Map>
        </div>
      </AccordionDetails>
    </Accordion>
  );
}
